import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useTheme } from '@/context/ThemeContext';
import PageSEO from '@/components/PageSEO';
import {
  ArrowLeft, Check, Loader2, Lock, Link2, RefreshCw, Table2, Trash2, Unlink, AlertTriangle,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const req = { withCredentials: true };
const COLORS = { google_ads: '#4285F4', ga4: '#E8710A', google_search_console: '#34A853', meta_ads: '#1877F2' };
const ABBR = { google_ads: 'Ads', ga4: 'GA4', google_search_console: 'GSC', meta_ads: 'f' };

const StatusChip = ({ provider }) => {
  const map = {
    connected: { label: 'CONNECTED', color: '#06D6A0' },
    credentials_pending: { label: 'CREDENTIALS PENDING', color: '#FFD166' },
    not_connected: { label: 'READY TO CONNECT', color: '#8b8b95' },
  };
  const s = map[provider.status] || map.not_connected;
  return (
    <span data-testid={`status-${provider.id}`} className="text-[10px] font-bold tracking-wider px-2 py-1 rounded"
      style={{ background: `${s.color}1a`, color: s.color }}>{s.label}</span>
  );
};

export default function ConnectionsPage() {
  const { isDark } = useTheme();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [providers, setProviders] = useState([]);
  const [picker, setPicker] = useState(null);          // { provider, label, accounts }
  const [pickerBusy, setPickerBusy] = useState(false);
  const [chosen, setChosen] = useState([]);
  const [schema, setSchema] = useState(null);
  const [sources, setSources] = useState([]);
  const [sourcesError, setSourcesError] = useState('');
  const [sheetUrl, setSheetUrl] = useState('');
  const [sheetName, setSheetName] = useState('');
  const [importing, setImporting] = useState(false);
  const [report, setReport] = useState(null);

  const T = {
    bg: isDark ? 'bg-zinc-950 text-zinc-100' : 'bg-zinc-50 text-zinc-900',
    card: isDark ? 'border-zinc-800 bg-zinc-900' : 'border-zinc-200 bg-white',
    muted: isDark ? 'text-zinc-400' : 'text-zinc-600',
    dim: isDark ? 'text-zinc-600' : 'text-zinc-400',
    input: isDark ? 'bg-zinc-950 border-zinc-800 text-zinc-200' : 'bg-white border-zinc-200 text-zinc-800',
  };

  const loadProviders = useCallback(async () => {
    const { data } = await axios.get(`${API}/api/connectors/providers`, req);
    setProviders(data);
  }, []);

  const loadSheets = useCallback(async () => {
    axios.get(`${API}/api/sheets/schema`, req).then(({ data }) => setSchema(data)).catch(() => setSchema(null));
    axios.get(`${API}/api/sheets/sources`, req)
      .then(({ data }) => { setSources(data); setSourcesError(''); })
      .catch(err => {
        const d = err?.response?.data?.detail;
        setSourcesError(typeof d === 'string' ? d : 'Could not load your existing sheet sources.');
      });
  }, []);

  useEffect(() => { loadProviders().catch(() => {}); loadSheets().catch(() => {}); }, [loadProviders, loadSheets]);

  const openPicker = useCallback(async (providerId) => {
    setPickerBusy(true);
    try {
      const { data } = await axios.get(`${API}/api/connectors/${providerId}/accounts`, req);
      setPicker({ provider: providerId, label: data.account_label, accounts: data.accounts });
      setChosen(data.accounts.filter(a => a.selected).map(a => a.external_account_id));
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error('Could not load accounts', { description: typeof d === 'string' ? d : d?.message || 'Try reconnecting.' });
    } finally { setPickerBusy(false); }
  }, []);

  useEffect(() => {
    const p = params.get('picker');
    if (p) { openPicker(p); params.delete('picker'); setParams(params, { replace: true }); }
  }, [params, setParams, openPicker]);

  const connect = async (provider) => {
    try {
      const redirect_uri = `${window.location.origin}/connections/callback`;
      const { data } = await axios.post(`${API}/api/connectors/${provider.id}/auth-url`, { redirect_uri }, req);
      sessionStorage.setItem('connector_provider', provider.id);
      sessionStorage.setItem('connector_redirect_uri', redirect_uri);
      window.location.href = data.url;
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (d?.missing_env) {
        toast.error(`${provider.name} is not switched on yet`, {
          description: `Add these to the backend environment: ${d.missing_env.join(', ')}. ${d.setup}`,
        });
      } else {
        toast.error('Could not start the connection', { description: typeof d === 'string' ? d : 'Please try again.' });
      }
    }
  };

  const saveAccounts = async () => {
    setPickerBusy(true);
    try {
      await axios.post(`${API}/api/connectors/${picker.provider}/accounts`, { account_ids: chosen }, req);
      toast.success('Accounts saved', { description: `${chosen.length} account(s) will be pulled into OmniView.` });
      setPicker(null);
      await loadProviders();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error('Could not save', { description: typeof d === 'string' ? d : 'Please try again.' });
    } finally { setPickerBusy(false); }
  };

  const disconnect = async (provider) => {
    if (!window.confirm(`Disconnect ${provider.name}? Stored tokens are deleted immediately.`)) return;
    await axios.post(`${API}/api/connectors/${provider.id}/disconnect`, {}, req);
    toast.success(`${provider.name} disconnected`);
    await loadProviders();
  };

  const importSheet = async (e) => {
    e.preventDefault();
    setImporting(true); setReport(null);
    try {
      const { data } = await axios.post(`${API}/api/sheets/sources`,
        { name: sheetName || 'Google Sheet', sheet_url: sheetUrl }, req);
      setReport(data.report);
      setSheetUrl(''); setSheetName('');
      toast.success('Sheet imported', { description: `${data.report.imported} row(s) in, ${data.report.rejected} rejected.` });
      await loadSheets();
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error('Import failed', { description: typeof d === 'string' ? d : 'Check the sheet link and sharing settings.' });
    } finally { setImporting(false); }
  };

  const syncSheet = async (id) => {
    try {
      const { data } = await axios.post(`${API}/api/sheets/sources/${id}/sync`, {}, req);
      setReport(data.report);
      toast.success('Re-synced', { description: `${data.report.imported} row(s) refreshed.` });
      await loadSheets();
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error('Sync failed', { description: typeof d === 'string' ? d : 'Check the sheet link.' });
    }
  };

  const removeSheet = async (id) => {
    if (!window.confirm('Remove this sheet and the rows it imported?')) return;
    await axios.delete(`${API}/api/sheets/sources/${id}`, req);
    toast.success('Sheet source removed');
    await loadSheets();
  };

  return (
    <div className={`min-h-screen ${T.bg}`}>
      <PageSEO title="Platform Connections" description="Connect Google Ads, GA4, Search Console and Meta Ads, or import from a Google Sheet." />
      <main id="main" className="max-w-4xl mx-auto px-4 sm:px-8 py-12">
        <button data-testid="back-to-dashboard" onClick={() => navigate('/dashboard')}
          className={`text-xs inline-flex items-center gap-1 mb-8 ${T.dim} hover:underline`}>
          <ArrowLeft size={13} /> Back to dashboard
        </button>

        <h1 className="text-4xl sm:text-5xl font-bold tracking-tight mb-4">Connections</h1>
        <p className={`text-base max-w-2xl mb-12 leading-relaxed ${T.muted}`}>
          Connect a platform once, then pick exactly which accounts OmniView is allowed to read.
          Tokens are encrypted at rest and never sent to the browser.
        </p>

        {/* ── OAuth providers ── */}
        <section data-testid="providers-section" className="space-y-4 mb-16">
          {providers.map(p => (
            <div key={p.id} data-testid={`provider-${p.id}`} className={`rounded-xl border p-6 ${T.card}`}>
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 rounded-lg flex items-center justify-center text-white text-xs font-bold"
                    style={{ background: COLORS[p.id] || '#002FA7' }}>{ABBR[p.id] || p.name.slice(0, 2).toUpperCase()}</div>
                  <div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <h2 className="text-base md:text-lg font-bold">{p.name}</h2>
                      <StatusChip provider={p} />
                    </div>
                    <p className={`text-sm mt-1 ${T.muted}`}>{p.account_label}</p>
                    {p.status === 'connected' && (
                      <p className={`text-xs mt-1 ${T.dim}`} data-testid={`selected-${p.id}`}>
                        {p.selected_accounts} account(s) selected
                      </p>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {p.status === 'connected' ? (
                    <>
                      <button data-testid={`manage-${p.id}`} onClick={() => openPicker(p.id)} disabled={pickerBusy}
                        className="px-3 py-2 rounded-md text-xs font-bold text-white disabled:opacity-60"
                        style={{ background: '#002FA7' }}>Choose accounts</button>
                      <button data-testid={`disconnect-${p.id}`} onClick={() => disconnect(p)}
                        className="px-3 py-2 rounded-md text-xs font-bold inline-flex items-center gap-1"
                        style={{ background: 'rgba(255,107,107,.1)', color: '#FF6B6B' }}>
                        <Unlink size={12} /> Disconnect
                      </button>
                    </>
                  ) : (
                    <button data-testid={`connect-${p.id}`} onClick={() => connect(p)}
                      className={`px-4 py-2 rounded-md text-xs font-bold inline-flex items-center gap-1 ${p.configured ? 'text-white' : ''}`}
                      style={p.configured
                        ? { background: 'linear-gradient(135deg,#002FA7,#00D4FF)' }
                        : { background: 'rgba(255,209,102,.12)', color: '#b98b12' }}>
                      {p.configured ? <><Link2 size={12} /> Connect</> : <><Lock size={12} /> Awaiting credentials</>}
                    </button>
                  )}
                </div>
              </div>
              {!p.configured && (
                <div data-testid={`pending-${p.id}`}
                  className="mt-4 rounded-lg p-4 text-xs leading-relaxed"
                  style={{ background: isDark ? 'rgba(255,209,102,.06)' : 'rgba(255,209,102,.12)' }}>
                  <p className="font-bold mb-1 flex items-center gap-1" style={{ color: '#b98b12' }}>
                    <AlertTriangle size={12} /> Live connection is wired but switched off
                  </p>
                  <p className={T.muted}>{p.setup}</p>
                  <p className={`mt-2 font-mono ${T.dim}`}>Needs: {p.missing_env.join(', ')}</p>
                </div>
              )}
            </div>
          ))}
          {!providers.length && (
            <div className={`rounded-xl border p-6 text-sm ${T.card} ${T.muted}`} data-testid="providers-empty">
              Loading providers…
            </div>
          )}
        </section>

        {/* ── Google Sheets fallback ── */}
        <section data-testid="sheets-section" className={`rounded-xl border p-6 sm:p-8 ${T.card}`}>
          <div className="flex items-center gap-2 mb-2">
            <Table2 size={18} style={{ color: '#0F9D58' }} />
            <h2 className="text-base md:text-lg font-bold">Google Sheets fallback</h2>
          </div>
          <p className={`text-sm mb-6 leading-relaxed ${T.muted}`}>
            For any platform without a native connector — marketplaces, quick-commerce, offline.
            Match the columns below, share the sheet with anyone-with-the-link, and paste the link.
            Nothing is uploaded; we read it server-side.
          </p>

          {schema && (
            <>
              <div className="overflow-x-auto mb-4">
                <table className="w-full text-xs" data-testid="sheet-schema-table">
                  <thead>
                    <tr className={`text-left ${T.dim}`}>
                      <th className="py-2 pr-4 font-bold">Column</th>
                      <th className="py-2 pr-4 font-bold">Type</th>
                      <th className="py-2 pr-4 font-bold">Required</th>
                      <th className="py-2 font-bold">Example</th>
                    </tr>
                  </thead>
                  <tbody>
                    {schema.columns.map(c => (
                      <tr key={c.name} className={`border-t ${isDark ? 'border-zinc-800' : 'border-zinc-100'}`}>
                        <td className="py-2 pr-4 font-mono">{c.name}</td>
                        <td className={`py-2 pr-4 ${T.muted}`}>{c.type}</td>
                        <td className="py-2 pr-4">{c.required
                          ? <span style={{ color: '#FF6B6B' }}>required</span>
                          : <span className={T.dim}>optional</span>}</td>
                        <td className={`py-2 font-mono ${T.muted}`}>{c.example}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <button data-testid="copy-sheet-header"
                onClick={() => { navigator.clipboard?.writeText(schema.template_csv)
                  .then(() => toast.success('Header row copied', { description: 'Paste into row 1 of a new Google Sheet.' }))
                  .catch(() => toast.error('Clipboard blocked', { description: 'Copy the column names from the table above.' })); }}
                className={`text-xs font-bold mb-8 px-3 py-2 rounded-md border ${isDark ? 'border-zinc-700' : 'border-zinc-300'}`}>
                Copy template header row
              </button>
            </>
          )}

          <form onSubmit={importSheet} className="grid sm:grid-cols-[1fr_2fr_auto] gap-3 mb-6">
            <input data-testid="sheet-name-input" value={sheetName} onChange={e => setSheetName(e.target.value)}
              placeholder="Source name" aria-label="Source name"
              className={`px-3 py-2 rounded-md text-sm border outline-none ${T.input}`} />
            <input data-testid="sheet-url-input" required value={sheetUrl} onChange={e => setSheetUrl(e.target.value)}
              placeholder="https://docs.google.com/spreadsheets/d/..." aria-label="Google Sheet link"
              className={`px-3 py-2 rounded-md text-sm border outline-none ${T.input}`} />
            <button data-testid="sheet-import-btn" type="submit" disabled={importing}
              className="px-5 py-2 rounded-md text-sm font-bold text-white disabled:opacity-60"
              style={{ background: '#0F9D58' }}>
              {importing ? <Loader2 size={14} className="animate-spin" /> : 'Import'}
            </button>
          </form>

          {report && (
            <div data-testid="import-report" className={`rounded-lg border p-4 mb-6 ${isDark ? 'border-zinc-800' : 'border-zinc-200'}`}>
              <p className="text-sm font-bold mb-2">
                {report.imported} imported · {report.rejected} rejected · {report.total_rows} rows read
              </p>
              {report.errors?.length ? (
                <ul className="space-y-1 text-xs" data-testid="import-errors">
                  {report.errors.map(err => (
                    <li key={err.row} className={T.muted}>
                      <span className="font-mono font-bold">Row {err.row}</span>
                      {err.preview?.date ? ` (${err.preview.date}${err.preview.platform ? ' · ' + err.preview.platform : ''})` : ''}
                      {' — '}{err.issues.join('; ')}
                    </li>
                  ))}
                  {report.truncated_errors > 0 && (
                    <li className={T.dim}>…and {report.truncated_errors} more rejected rows.</li>
                  )}
                </ul>
              ) : (
                <p className="text-xs inline-flex items-center gap-1" style={{ color: '#06D6A0' }}>
                  <Check size={12} /> Every row passed validation.
                </p>
              )}
            </div>
          )}

          {sourcesError && (
            <p data-testid="sheet-sources-error" className="text-xs mb-4" style={{ color: '#FF6B6B' }}>{sourcesError}</p>
          )}

          {sources.length > 0 && (
            <div data-testid="sheet-sources" className="space-y-2">
              {sources.map(s => (
                <div key={s.id} data-testid={`sheet-source-${s.id}`}
                  className={`flex flex-wrap items-center justify-between gap-3 rounded-lg border p-3 ${isDark ? 'border-zinc-800' : 'border-zinc-200'}`}>
                  <div className="min-w-0">
                    <p className="text-sm font-bold truncate">{s.name}</p>
                    <p className={`text-xs truncate ${T.dim}`}>
                      {s.rows_imported ?? 0} rows · {s.rows_rejected ?? 0} rejected
                      {s.last_sync ? ` · synced ${new Date(s.last_sync).toLocaleString()}` : ''}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <button data-testid={`sync-${s.id}`} onClick={() => syncSheet(s.id)}
                      className="px-3 py-2 rounded-md text-xs font-bold inline-flex items-center gap-1"
                      style={{ background: 'rgba(15,157,88,.12)', color: '#0F9D58' }}>
                      <RefreshCw size={12} /> Re-sync
                    </button>
                    <button data-testid={`remove-${s.id}`} onClick={() => removeSheet(s.id)}
                      className="px-3 py-2 rounded-md text-xs font-bold inline-flex items-center gap-1"
                      style={{ background: 'rgba(255,107,107,.1)', color: '#FF6B6B' }}>
                      <Trash2 size={12} /> Remove
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

        <p className={`text-xs mt-8 ${T.dim}`}>
          Need a platform we do not list? <Link to="/blog" className="underline">Read how the fallback schema works</Link>.
        </p>
      </main>

      {/* ── Account picker ── */}
      {picker && (
        <div data-testid="account-picker" className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
          style={{ background: 'rgba(0,0,0,.65)', backdropFilter: 'blur(6px)' }}
          onClick={() => setPicker(null)}>
          <div onClick={e => e.stopPropagation()}
            className={`w-full max-w-lg rounded-2xl border p-7 max-h-[80vh] overflow-y-auto ${T.card}`}>
            <h3 className="text-base md:text-lg font-bold mb-1">Choose {picker.label}</h3>
            <p className={`text-sm mb-5 ${T.muted}`}>
              Only the accounts you tick are read. Nothing is pulled by default.
            </p>
            <div className="space-y-2 mb-6">
              {picker.accounts.map(a => {
                const on = chosen.includes(a.external_account_id);
                return (
                  <label key={a.external_account_id} data-testid={`account-${a.external_account_id}`}
                    className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer ${isDark ? 'border-zinc-800' : 'border-zinc-200'}`}>
                    <input type="checkbox" checked={on} className="accent-blue-700"
                      onChange={() => setChosen(c => on ? c.filter(x => x !== a.external_account_id) : [...c, a.external_account_id])} />
                    <span className="min-w-0">
                      <span className="block text-sm font-bold truncate">{a.name}</span>
                      <span className={`block text-xs font-mono ${T.dim}`}>
                        {a.external_account_id}{a.currency ? ` · ${a.currency}` : ''}
                      </span>
                    </span>
                  </label>
                );
              })}
              {!picker.accounts.length && (
                <p className={`text-sm ${T.muted}`} data-testid="no-accounts">
                  This login cannot see any accounts on that platform.
                </p>
              )}
            </div>
            <div className="flex gap-3">
              <button data-testid="save-accounts" onClick={saveAccounts} disabled={pickerBusy || !chosen.length}
                className="px-5 py-2 rounded-md text-sm font-bold text-white disabled:opacity-50"
                style={{ background: 'linear-gradient(135deg,#002FA7,#00D4FF)' }}>
                {pickerBusy ? <Loader2 size={14} className="animate-spin" /> : `Save ${chosen.length} account(s)`}
              </button>
              <button data-testid="cancel-picker" onClick={() => setPicker(null)}
                className={`px-5 py-2 rounded-md text-sm font-bold border ${isDark ? 'border-zinc-700' : 'border-zinc-300'}`}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
